import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import {
  awaitingReply,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES,
  type DocumentType,
  describeError,
  downloadUrl,
  fullDate,
  isCorrespondence,
  photoErrorMessage,
  present,
  replyDirection,
  today,
  uploadFile,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  ConfigProvider,
  Image,
  Input,
  Progress,
  Select,
  Skeleton,
  Tag,
} from 'antd';
import { useRef, useState } from 'react';
import { useClient, useQuery } from 'urql';
import {
  AddFileDocument,
  CreateDocumentDocument,
  DeleteDocumentDocument,
  type DocumentDetail,
  DocumentDocument,
  documentsContext,
  LettersDocument,
  MoveFileDocument,
  RemoveFileDocument,
  UpdateDocumentDocument,
  useCanFile,
} from './association-data';
import { useLists } from './data';
import { DeskPanel, useDirty } from './panel';
import { CounterpartyPicker, ProjectPicker } from './pickers';
import { KeyValues, Status, useEditKey, ViewBar } from './ui';

type Values = {
  type: DocumentType | null;
  title: string;
  documentDate: string;
  reference: string;
  counterpartyId: string | null;
  projectId: string | null;
  notes: string;
  replyDueOn: string;
  /** The letter this one answers (its `repliedById` points here). */
  replyToId: string | null;
};

/** A file shown in the form: saved (`id`) or sent but not attached yet. */
type FileItem = {
  key: string;
  id: string | null;
  photoId: string;
  url: string;
  pdf: boolean;
};

const DOCUMENT_ERRORS = {
  PHOTO_WRONG_KIND: 'هذا الملف ليس من الوثائق. أرسله من جديد.',
  PHOTO_NOT_FOUND: 'الملف لم يعد موجوداً. أرسله من جديد.',
  'CHECK_VIOLATION:association_documents_title_required': 'اكتب عنوان الوثيقة.',
};

const valuesOf = (d: DocumentDetail): Values => ({
  type: d.type,
  title: d.title,
  documentDate: d.documentDate,
  reference: d.reference ?? '',
  counterpartyId: d.counterpartyId,
  projectId: d.projectId,
  notes: d.notes ?? '',
  replyDueOn: d.replyDueOn ?? '',
  replyToId: d.replyTo.nodes[0]?.id ?? null,
});

const EMPTY: Values = {
  type: null,
  title: '',
  documentDate: today(),
  reference: '',
  counterpartyId: null,
  projectId: null,
  notes: '',
  replyDueOn: '',
  replyToId: null,
};

/**
 * One document beside the library: its details, its files in order (photos
 * one per page, or a PDF), and the letters it answers or that answer it.
 * Letters left without a number are numbered «N/YYYY» by arqam.
 */
export function DocumentPanel({
  id,
  presetType,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  presetType: DocumentType | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [{ data, error }] = useQuery({
    query: DocumentDocument,
    variables: { id: id ?? '' },
    pause: !id,
    context: documentsContext,
  });
  const doc = data?.associationDocument ?? null;
  if (id && !doc) {
    return (
      <DeskPanel title="الوثيقة" onClose={onClose}>
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل الوثيقة" />
        ) : data ? (
          <Alert type="warning" showIcon message="الوثيقة غير موجودة" />
        ) : (
          <Skeleton active paragraph={{ rows: 10 }} />
        )}
      </DeskPanel>
    );
  }
  return (
    <DocumentForm
      key={doc?.id ?? 'new'}
      doc={doc}
      initial={doc ? valuesOf(doc) : { ...EMPTY, type: presetType }}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
    />
  );
}

function DocumentForm({
  doc,
  initial,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  doc: DocumentDetail | null;
  initial: Values;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canFile = useCanFile();
  const client = useClient();
  const { message, modal } = App.useApp();
  const [start, setStart] = useState(initial);
  const [values, setValues] = useState(initial);
  // Read first (doc 61); a new document opens in its form.
  const [editing, setEditing] = useState(!doc);
  useEditKey(!editing && canFile, () => setEditing(true));
  const [staged, setStaged] = useState<FileItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useDirty(
    { values, staged: staged.length },
    { values: start, staged: 0 },
    onDirtyChange,
  );
  const set = (patch: Partial<Values>) =>
    setValues((v) => ({ ...v, ...patch }));
  const letter = isCorrespondence(values.type);
  const other = values.type ? replyDirection(values.type) : null;
  const [{ data: letters }] = useQuery({
    query: LettersDocument,
    variables: { type: other ?? 'CORRESPONDENCE_OUT' },
    pause: !other,
    context: documentsContext,
  });
  const candidates = present(letters?.associationDocuments?.nodes).filter(
    (l) => l.id !== doc?.id && (!l.repliedById || l.repliedById === doc?.id),
  );

  const mutate = async <R,>(
    run: () => Promise<{
      error?: Parameters<typeof describeError>[0];
      data?: R;
    }>,
  ) => {
    const r = await run();
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر الحفظ.', DOCUMENT_ERRORS));
      return null;
    }
    return r.data ?? ({} as R);
  };

  async function save() {
    if (!editing) return;
    if (!canFile || saving) return;
    setProblem(null);
    if (!values.type) return setProblem('اختر نوع الوثيقة.');
    if (!values.title.trim()) return setProblem('اكتب عنوان الوثيقة.');
    if (!values.documentDate) return setProblem('اختر تاريخ الوثيقة.');
    const fields = {
      type: values.type,
      title: values.title.trim(),
      documentDate: values.documentDate,
      reference: values.reference.trim() || null,
      counterpartyId: values.counterpartyId,
      projectId: values.projectId,
      notes: values.notes.trim() || null,
      replyDueOn: letter ? values.replyDueOn || null : null,
    };
    setSaving(true);
    try {
      let id = doc?.id ?? null;
      let reference = fields.reference;
      if (id) {
        const r = await mutate(() =>
          client
            .mutation(
              UpdateDocumentDocument,
              { id: id as string, patch: fields },
              documentsContext,
            )
            .toPromise(),
        );
        if (!r) return;
        reference =
          r.updateAssociationDocument?.associationDocument?.reference ?? null;
      } else {
        const r = await mutate(() =>
          client
            .mutation(CreateDocumentDocument, { doc: fields }, documentsContext)
            .toPromise(),
        );
        const created = r?.createAssociationDocument?.associationDocument;
        if (!created) return;
        id = created.id;
        reference = created.reference;
        // Files sent before the document existed, in their order.
        for (const [i, f] of staged.entries()) {
          const added = await mutate(() =>
            client
              .mutation(
                AddFileDocument,
                {
                  file: {
                    documentId: id as string,
                    photoId: f.photoId,
                    position: i + 1,
                  },
                },
                documentsContext,
              )
              .toPromise(),
          );
          if (!added) return;
        }
        setStaged([]);
      }
      // «هذا رد على…»: the original letter points to this one.
      if (values.replyToId !== start.replyToId) {
        if (start.replyToId) {
          await mutate(() =>
            client
              .mutation(
                UpdateDocumentDocument,
                { id: start.replyToId as string, patch: { repliedById: null } },
                documentsContext,
              )
              .toPromise(),
          );
        }
        if (values.replyToId) {
          const linked = await mutate(() =>
            client
              .mutation(
                UpdateDocumentDocument,
                { id: values.replyToId as string, patch: { repliedById: id } },
                documentsContext,
              )
              .toPromise(),
          );
          if (!linked) return;
        }
      }
      setStart(values);
      setEditing(false);
      message.success(
        letter && reference && !fields.reference
          ? `حُفظت. رقم المراسلة: ${reference}`
          : 'حُفظت الوثيقة',
      );
      if (!doc && id) {
        onDirtyChange(false);
        onOpen(id);
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!doc) return;
    const ok = await modal.confirm({
      title: 'حذف الوثيقة',
      content: `«${doc.title}» وملفاتها. لا يمكن التراجع.`,
      okText: 'احذف',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await client
      .mutation(DeleteDocumentDocument, { id: doc.id }, documentsContext)
      .toPromise();
    if (r.error) {
      message.error(describeError(r.error, 'تعذّر الحذف.', DOCUMENT_ERRORS));
      return;
    }
    message.success('حُذفت الوثيقة');
    onDirtyChange(false);
    onClose();
  }

  return (
    <DeskPanel
      title={doc ? DOCUMENT_TYPE_LABELS[doc.type] : 'وثيقة جديدة'}
      recordId={doc?.id}
      onClose={onClose}
      onSave={save}
    >
      {doc && (
        <div className="desk-panel-tags">
          {doc.reference && <Tag className="ltr-num">{doc.reference}</Tag>}
          {doc.isCurrent && <Tag color="green">النسخة المعتمدة</Tag>}
        </div>
      )}
      {!canFile && <Alert type="info" showIcon message="للاطلاع فقط." />}

      <Files doc={doc} staged={staged} onStaged={setStaged} canFile={canFile} />

      {!editing && doc ? (
        <DocumentView
          values={start}
          doc={doc}
          onEdit={canFile ? () => setEditing(true) : undefined}
          onDelete={canFile ? remove : undefined}
        />
      ) : (
        <ConfigProvider componentDisabled={!canFile}>
          <form
            className="desk-form"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <label htmlFor="doc-type">النوع</label>
            <Select
              id="doc-type"
              showSearch
              optionFilterProp="label"
              placeholder="اختر النوع"
              value={values.type ?? undefined}
              onChange={(type) =>
                set({
                  type,
                  replyToId: isCorrespondence(type) ? values.replyToId : null,
                })
              }
              options={DOCUMENT_TYPES.map((t) => ({
                value: t,
                label: DOCUMENT_TYPE_LABELS[t],
              }))}
            />
            <label htmlFor="doc-title">العنوان</label>
            <Input
              id="doc-title"
              autoFocus={!doc}
              placeholder="مثلاً: طلب ترخيص حفر بئر"
              value={values.title}
              onChange={(e) => set({ title: e.target.value })}
            />
            <div className="desk-fields">
              <label htmlFor="doc-date">التاريخ</label>
              <label htmlFor="doc-ref">المرجع</label>
              <Input
                id="doc-date"
                type="date"
                className="ltr-num"
                value={values.documentDate}
                onChange={(e) => set({ documentDate: e.target.value })}
              />
              <Input
                id="doc-ref"
                className="ltr-num"
                placeholder={letter ? 'يُرقَّم تلقائياً' : 'اختياري'}
                value={values.reference}
                onChange={(e) => set({ reference: e.target.value })}
              />
              <label htmlFor="doc-counterparty">
                {values.type === 'CORRESPONDENCE_IN'
                  ? 'المرسِل'
                  : values.type === 'CORRESPONDENCE_OUT'
                    ? 'المرسَل إليه'
                    : 'الجهة'}
              </label>
              <label htmlFor="doc-project">المشروع</label>
              <CounterpartyPicker
                id="doc-counterparty"
                type={null}
                kind="institution"
                placeholder="الجماعة، القيادة، شريك…"
                value={values.counterpartyId}
                onChange={(counterpartyId) => set({ counterpartyId })}
              />
              <ProjectPicker
                id="doc-project"
                clearable
                keep={start.projectId}
                placeholder="اختياري"
                value={values.projectId}
                onChange={(projectId) => set({ projectId })}
              />
            </div>
            {letter && (
              <div className="desk-fields">
                <label htmlFor="doc-due">ننتظر الرد قبل</label>
                <label htmlFor="doc-reply-to">هذا رد على…</label>
                <Input
                  id="doc-due"
                  type="date"
                  className="ltr-num"
                  value={values.replyDueOn}
                  onChange={(e) => set({ replyDueOn: e.target.value })}
                />
                <Select
                  id="doc-reply-to"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={
                    values.type === 'CORRESPONDENCE_IN'
                      ? 'رسالة صادرة'
                      : 'رسالة واردة'
                  }
                  value={values.replyToId ?? undefined}
                  onChange={(replyToId) =>
                    set({ replyToId: replyToId ?? null })
                  }
                  options={candidates.map((l) => ({
                    value: l.id,
                    label: `${l.reference ?? ''} ${l.title} (${l.documentDate})`,
                  }))}
                />
              </div>
            )}
            <label htmlFor="doc-notes">ملاحظات</label>
            <Input.TextArea
              id="doc-notes"
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={values.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
            {problem && <Alert type="error" showIcon message={problem} />}
            {canFile && (
              <div className="desk-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  title="Ctrl+S"
                >
                  حفظ
                </Button>
                <span className="desk-actions-gap" />
                {doc && (
                  <Button
                    onClick={() => {
                      setValues(start);
                      setProblem(null);
                      setEditing(false);
                    }}
                  >
                    إلغاء
                  </Button>
                )}
              </div>
            )}
          </form>
        </ConfigProvider>
      )}

      {doc && <ReplyChain doc={doc} onOpen={onOpen} />}
      {doc && (
        <div className="field-hint desk-meta">
          أُدخلت {fullDate(doc.createdAt)}
          {doc.createdBy && ` · ${doc.createdBy}`}
        </div>
      )}
    </DeskPanel>
  );
}

function ReplyChain({
  doc,
  onOpen,
}: {
  doc: DocumentDetail;
  onOpen: (id: string) => void;
}) {
  const original = doc.replyTo.nodes[0];
  const reply = doc.repliedBy;
  if (!original && !reply) return null;
  const line = (
    label: string,
    d: {
      id: string;
      title: string;
      reference: string | null;
      documentDate: string;
    },
  ) => (
    <button type="button" className="desk-line" onClick={() => onOpen(d.id)}>
      <span>{label}</span>
      <span>
        {d.reference && <span className="ltr-num">{d.reference} · </span>}
        {d.title}
      </span>
      <span className="ltr-num field-hint">{d.documentDate}</span>
    </button>
  );
  return (
    <section className="desk-reply-chain">
      <h3 className="desk-panel-subtitle">المراسلة</h3>
      {original && line('ردّ على', original)}
      {reply && line('الرد', reply)}
    </section>
  );
}

/**
 * The files: one PDF, or a photo per page in order. Drop or choose several
 * at once; each is sent to arqam (`kind=DOCUMENT`) then attached. A new
 * document keeps them until it is saved.
 */
function Files({
  doc,
  staged,
  onStaged,
  canFile,
}: {
  doc: DocumentDetail | null;
  staged: FileItem[];
  onStaged: (f: FileItem[]) => void;
  canFile: boolean;
}) {
  const client = useClient();
  const { message } = App.useApp();
  const input = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState<{
    done: number;
    total: number;
    progress: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  const saved: FileItem[] = present(doc?.files.nodes).flatMap((f) =>
    f.photo
      ? [
          {
            key: f.id,
            id: f.id,
            photoId: f.photoId,
            url: f.photo.url,
            pdf: f.photo.contentType === 'application/pdf',
          },
        ]
      : [],
  );
  const items = doc ? saved : staged;
  const positions = present(doc?.files.nodes).map((f) => f.position);
  let next = (positions.length ? Math.max(...positions) : 0) + 1;

  async function add(files: File[]) {
    if (!files.length) return;
    const added: FileItem[] = [];
    for (const [i, file] of files.entries()) {
      setSending({ done: i, total: files.length, progress: 0 });
      try {
        const photo = await uploadFile(file, 'DOCUMENT', (progress) =>
          setSending({ done: i, total: files.length, progress }),
        );
        if (doc) {
          const r = await client
            .mutation(
              AddFileDocument,
              {
                file: { documentId: doc.id, photoId: photo.id, position: next },
              },
              documentsContext,
            )
            .toPromise();
          if (r.error) {
            message.error(
              describeError(r.error, 'تعذّر إضافة الملف.', DOCUMENT_ERRORS),
            );
            break;
          }
          next += 1;
        } else {
          added.push({
            key: photo.id,
            id: null,
            photoId: photo.id,
            url: photo.url,
            pdf: photo.pdf,
          });
        }
      } catch (error) {
        message.error(`${file.name}: ${photoErrorMessage(error)}`);
        break;
      }
    }
    setSending(null);
    if (!doc && added.length) onStaged([...staged, ...added]);
  }

  async function move(index: number, step: -1 | 1) {
    const target = index + step;
    if (target < 0 || target >= items.length) return;
    if (!doc) {
      const reordered = [...staged];
      [reordered[index], reordered[target]] = [
        reordered[target],
        reordered[index],
      ];
      onStaged(reordered);
      return;
    }
    // Positions 1…n in the new order.
    const order = [...items];
    [order[index], order[target]] = [order[target], order[index]];
    for (const [i, f] of order.entries()) {
      if (!f.id) continue;
      const r = await client
        .mutation(
          MoveFileDocument,
          { id: f.id, position: i + 1 },
          documentsContext,
        )
        .toPromise();
      if (r.error) {
        message.error(describeError(r.error, 'تعذّر الترتيب.'));
        return;
      }
    }
  }

  async function removeAt(index: number) {
    const f = items[index];
    if (!doc || !f.id) {
      onStaged(staged.filter((_, i) => i !== index));
      return;
    }
    const r = await client
      .mutation(RemoveFileDocument, { id: f.id }, documentsContext)
      .toPromise();
    if (r.error) message.error(describeError(r.error, 'تعذّر الحذف.'));
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a drop zone; the button inside opens the file chooser
    <section
      className={`desk-doc-files${dragging ? ' is-dragging' : ''}`}
      onDragOver={(e) => {
        if (!canFile) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!canFile) return;
        e.preventDefault();
        setDragging(false);
        add([...e.dataTransfer.files]);
      }}
    >
      {items.length > 0 && (
        <Image.PreviewGroup>
          <ol className="desk-doc-pages">
            {items.map((f, i) => (
              <li key={f.key}>
                {f.pdf ? (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="desk-doc-pdf"
                  >
                    <FilePdfOutlined />
                    <span>PDF</span>
                  </a>
                ) : (
                  <Image src={f.url} alt={`الصفحة ${i + 1}`} />
                )}
                <div className="desk-doc-page-bar">
                  <span className="ltr-num">{i + 1}</span>
                  {f.id && (
                    <a
                      href={downloadUrl(f.url)}
                      title="تنزيل"
                      aria-label="تنزيل"
                    >
                      <DownloadOutlined />
                    </a>
                  )}
                  {canFile && (
                    <>
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={i === 0}
                        title="قبلها"
                        aria-label="قبلها"
                        onClick={() => move(i, -1)}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={i === items.length - 1}
                        title="بعدها"
                        aria-label="بعدها"
                        onClick={() => move(i, 1)}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        title="إزالة"
                        aria-label="إزالة"
                        onClick={() => removeAt(i)}
                      />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Image.PreviewGroup>
      )}
      {sending && (
        <Progress
          percent={Math.round(
            ((sending.done + sending.progress) / sending.total) * 100,
          )}
          format={() => `${sending.done + 1} / ${sending.total}`}
        />
      )}
      {canFile && (
        <button
          type="button"
          className="desk-doc-drop"
          onClick={() => input.current?.click()}
          disabled={!!sending}
        >
          <InboxOutlined />
          <span>
            {items.length ? 'إضافة صفحات أو ملفات' : 'الملفات'}: اسحبها هنا أو
            اخترها. صور (صفحة لكل صورة) أو PDF حتى 10 ميغابايت.
          </span>
        </button>
      )}
      {!canFile && items.length === 0 && (
        <div className="field-hint">لا ملفات لهذه الوثيقة.</div>
      )}
      <input
        ref={input}
        type="file"
        multiple
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => {
          add([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </section>
  );
}

/** A document read (doc 61): its facts, then «تعديل (E)» and «حذف». */
function DocumentView({
  values: v,
  doc,
  onEdit,
  onDelete,
}: {
  values: Values;
  doc: DocumentDetail;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [{ data: lists }] = useLists();
  const name = (
    pool: ({ id: string; name: string } | null)[] | undefined,
    id: string | null,
  ) => (id ? present(pool).find((x) => x.id === id)?.name : null);
  const letter = isCorrespondence(v.type);
  const original = doc.replyTo.nodes[0];
  return (
    <div className="ui-stack">
      <KeyValues
        columns={2}
        items={[
          { label: 'العنوان', value: v.title, wide: true },
          { label: 'النوع', value: v.type && DOCUMENT_TYPE_LABELS[v.type] },
          { label: 'التاريخ', value: fullDate(v.documentDate) },
          {
            label: 'المرجع',
            value: v.reference && (
              <span className="ltr-num">{v.reference}</span>
            ),
          },
          {
            label:
              v.type === 'CORRESPONDENCE_IN'
                ? 'المرسِل'
                : v.type === 'CORRESPONDENCE_OUT'
                  ? 'المرسَل إليه'
                  : 'الجهة',
            value: name(lists?.counterparties?.nodes, v.counterpartyId),
          },
          {
            label: 'المشروع',
            value: name(lists?.projects?.nodes, v.projectId),
          },
          letter && {
            label: 'ننتظر الرد قبل',
            value: v.replyDueOn ? (
              awaitingReply(
                { replyDueOn: v.replyDueOn, repliedById: doc.repliedById },
                today(),
              ) ? (
                <Status tone="error">
                  {fullDate(v.replyDueOn)}: فات الموعد
                </Status>
              ) : doc.repliedById ? (
                <Status tone="success">وصل الرد</Status>
              ) : (
                fullDate(v.replyDueOn)
              )
            ) : null,
          },
          letter && {
            label: 'هذا رد على',
            value: original && `${original.reference ?? ''} ${original.title}`,
          },
          { label: 'ملاحظات', value: v.notes, wide: true },
        ]}
      />
      <ViewBar onEdit={onEdit}>
        {onDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
            حذف
          </Button>
        )}
      </ViewBar>
    </div>
  );
}
